import { ResponsiveService } from '@cue-admin/ui-directives';
import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone,
  OnDestroy,
  inject,
  OnInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from '@cue-admin/account-data';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { NotificationRef, NotificationService } from '@progress/kendo-angular-notification';
import { fromEvent, merge, Subject, takeUntil, take } from 'rxjs';
import { HeaderActionsComponent } from '../header-actions/header-actions.component';
import { MenuComponent } from '../menu/menu.component';
import { AdminLayoutStateService } from '@cue-admin/layout-state';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HeaderActionsComponent, MenuComponent, RouterOutlet, AsyncPipe],
})
export class LayoutComponent implements OnInit, OnDestroy {
  translate = inject(TranslateService);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private zone = inject(NgZone);
  private accountService = inject(AccountService);
  private document = inject<Document>(DOCUMENT);
  responsiveService = inject(ResponsiveService);
  layoutState = inject(AdminLayoutStateService);
  private cdr = inject(ChangeDetectorRef);

  appTitle = 'CUE Admin';

  reset$: Subject<any> = new Subject();
  public menuCollapsed$ = this.layoutState.menuCollapsed$;
  public headerHidden$ = this.layoutState.headerHidden$;

  private _idleInterval!: any;
  private _idleTime!: any;
  private _show: NotificationRef | null = null;
  ngOnInit() {
    const translate = this.translate;
    const responsiveService = this.responsiveService;

    translate.addLangs(['en', 'cs']);
    translate.setDefaultLang('en');
    if (!localStorage.getItem('currentLanguage')) {
      const browserLang = translate.getBrowserLang();
      const newLang = browserLang?.match(/en|cs/) ? browserLang : 'en';
      localStorage.setItem('currentLanguage', newLang);
    }
    const currentLanguage = localStorage.getItem('currentLanguage') ?? 'en';
    translate.use(currentLanguage);
    translate.currentLang = currentLanguage;
    this.clear();
    this.resetTime();
    this.zone.runOutsideAngular(() => {
      this.document.onload = this.resetTime.bind(this);
      merge(
        fromEvent(this.document, 'load'),
        fromEvent(this.document, 'mousemove'),
        fromEvent(this.document, 'mousedown'),
        fromEvent(this.document, 'touchstart'),
        fromEvent(this.document, 'touchmove'),
        fromEvent(this.document, 'click'),
        fromEvent(this.document, 'keydown'),
        fromEvent(window, 'scroll')
      )
        .pipe(takeUntil(this.reset$))
        .subscribe(() => {
          this.resetTime();
        });
    });

    responsiveService.isTablet().subscribe((result: any) => {
      this.menuCollapsed$.next(result);
      this.cdr.markForCheck();
    });
  }

  resetTime() {
    this._idleTime = 0;
    if (this._show) {
      const notificationRef = this._show;
      this.zone.run(() => {
        notificationRef.hide();
        this._show = null;
      });
    }
    this.clear();
    this._idleInterval = setInterval(this.timerIncrement.bind(this), 10000); // kazdych 10 sec
  }

  clear() {
    if (this._idleInterval) {
      clearInterval(this._idleInterval);
    }
  }

  timerIncrement() {
    this._idleTime = this._idleTime + 10;
    if (this._idleTime > 60 * 20) {
      this.zone.run(() => {
        if (!this._show) {
          this._show = this.notificationService.show({
            content: this.translate.instant('portal.autoLogout'),
            hideAfter: 1000 * 60 * 30,
            position: { horizontal: 'center', vertical: 'top' },
            type: { style: 'warning', icon: true },
            animation: {
              type: 'fade',
              duration: 500,
            },
          });
        }
      });
    }
    if (this._idleTime > 60 * 30) {
      // vice nez 30 minut minutes
      this.zone.run(() => {
        if (this._show) {
          const notificationRef = this._show;
          this.zone.run(() => {
            notificationRef.hide();
          });
        }
        this.clear();
        this.accountService.actionLogout().subscribe(() => {
          this.router.navigateByUrl('/account/login');
        });
      });
    }
  }

  menuCollapsing(): void {
    this.menuCollapsed$.next(!this.menuCollapsed$.value);
  }

  ngOnDestroy(): void {
    this.reset$.next(null);
    this.reset$.complete();
    this.clear();
  }

  closeMobileMenuWithOverlay() {
    this.menuCollapsed$.pipe(take(1)).subscribe((menuCollapsed: any) => {
      this.responsiveService
        .isTablet()
        .pipe(take(1))
        .subscribe((isMobile: any) => {
          if (!menuCollapsed && isMobile) {
            this.menuCollapsing();
          }
        });
    });
  }
}
